/**
 * @description Audit log viewer for Lynx Admin
 *
 * Key features:
 * - Filterable table: date range, action, severity, entity type
 * - Sortable by date, severity, risk score
 * - Expandable rows for full metadata (request ID, session, compliance category)
 * - Pagination with offset/limit
 *
 * @dependencies
 * - adminApi: getAuditLogs()
 * - lucide-react: icons
 *
 * @notes
 * - Data from GET /api/audit-logs with query filters
 * - Response: { auditLogs: [...], pagination: { total, limit, offset, hasMore } }
 * - Superadmin and SO roles allowed (enforced by backend middleware)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  ScrollText,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Filter,
  X,
} from 'lucide-react';
import { getAuditLogs } from '../../services/api/adminApi';

const SEVERITY_STYLES = {
  critical: 'bg-red-50 text-red-700',
  high: 'bg-orange-50 text-orange-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-blue-50 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

const PAGE_SIZE = 50;

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: PAGE_SIZE, offset: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      };

      if (actionFilter) params.action = actionFilter;
      if (severityFilter) params.severity = severityFilter;
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const result = await getAuditLogs(params);
      setLogs(result.auditLogs || []);
      setPagination(result.pagination || { total: 0, limit: PAGE_SIZE, offset: 0, hasMore: false });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, severityFilter, entityTypeFilter, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const clearFilters = () => {
    setActionFilter('');
    setSeverityFilter('');
    setEntityTypeFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const hasActiveFilters = actionFilter || severityFilter || entityTypeFilter || startDate || endDate;

  const totalPages = Math.ceil(pagination.total / PAGE_SIZE);
  const currentPage = page + 1;

  if (error && !loading && logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load audit logs</p>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {pagination.total.toLocaleString()} total entries
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 transition-colors"
            >
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="Action..."
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
            />
          </div>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
          <input
            type="text"
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            placeholder="Entity type..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {hasActiveFilters ? 'No logs match your filters' : 'No audit logs recorded yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="w-8 px-2 py-3" />
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map((log) => (
                  <>
                    <tr
                      key={log.id}
                      onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-2 py-3 text-center">
                        {expandedId === log.id
                          ? <ChevronDown className="w-4 h-4 text-gray-400 mx-auto" />
                          : <ChevronRight className="w-4 h-4 text-gray-400 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info
                        }`}>
                          {log.severity || 'info'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {log.action || '--'}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{log.entityType || '--'}</p>
                        <p className="text-xs text-gray-400">{log.entityId || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.userId || '--'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                        {log.ipAddress || '--'}
                      </td>
                    </tr>
                    {expandedId === log.id && (
                      <tr key={`${log.id}-detail`}>
                        <td colSpan={7} className="bg-gray-50 px-8 py-4">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Request ID</span>
                              <span className="text-gray-700 font-mono text-xs">{log.requestId || '--'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Session ID</span>
                              <span className="text-gray-700 font-mono text-xs">{log.sessionId || '--'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Compliance Category</span>
                              <span className="text-gray-700">{log.complianceCategory || '--'}</span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Risk Score</span>
                              <span className={`font-medium ${
                                (log.riskScore || 0) >= 70 ? 'text-red-600' :
                                (log.riskScore || 0) >= 40 ? 'text-amber-600' : 'text-green-600'
                              }`}>
                                {log.riskScore ?? '--'}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Operation Result</span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                log.operationResult === 'success'
                                  ? 'bg-green-50 text-green-700'
                                  : log.operationResult === 'failure'
                                    ? 'bg-red-50 text-red-700'
                                    : 'bg-gray-100 text-gray-600'
                              }`}>
                                {log.operationResult || '--'}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block mb-0.5">Data Classification</span>
                              <span className="text-gray-700">{log.dataClassification || '--'}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-xs text-gray-400 block mb-0.5">User Agent</span>
                              <span className="text-gray-500 text-xs truncate block">{log.userAgent || '--'}</span>
                            </div>
                            {log.details && (
                              <div className="col-span-2 lg:col-span-4">
                                <span className="text-xs text-gray-400 block mb-0.5">Details</span>
                                <pre className="text-xs text-gray-600 bg-white border border-gray-200 rounded-lg p-3 overflow-auto max-h-32">
                                  {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, pagination.total)} of {pagination.total.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-sm text-gray-600 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.hasMore}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AuditLogs;
